<script setup>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import { computed } from 'vue'
import Tooltip from '@/components/Tooltip.vue'

const props = defineProps({
    platformId: {
        type: String,
    },
    contentType: {
        type: String,
    },
    data: {
        type: Object,
        required: true,
    },
    row: {
        type: Object,
        required: true,
    },
    isNormalized: {
        type: Boolean,
        required: false,
    },
    link: {
        type: String,
    },
})

// Get effective content type: prefer data's content_type, fall back to prop
const effectiveContentType = computed(() => {
    return props.data?.content_type || props.contentType
})

// Get effective content sub type for Instagram stories
const effectiveContentSubType = computed(() => {
    return props.data?.content_sub_type || props.contentType
})

const mappedData = computed(() => {

    if (props.platformId === 'twitch') {
            if (effectiveContentType.value === 'stream') {
                return {
                    image: `${MEDIA_CDN_BASE}/${
                        props.platformId
                    }/games/thumbnails/default/${encodeURI(props.data?.game_id)}`,
                    gameId: props.data?.game_id,
                    isSponsored: props.data.is_sponsored,
                    title: props.data.title,
                    subTitleText: props.data.game_name,
                }
            }
        }
        if (props.platformId === 'youtube') {
            return {
                image: props.data?.thumbnail,
                gameId: props.data?.game_id,
                isSponsored: props.data.is_sponsored,
                title: props.data.title,
                subTitleText: props.data.game_name
                    ? props.data.game_name
                    : _.map(props.data?.tags, (item) => `#${item}`).join(' '),
            }
        }

        if (props.platformId === 'tiktok') {
            const hashtags = _.map(
                props.data?.tags?.filter((item) => item),
                (item) => `#${item}`,
            )

            return {
                image: props.data.thumbnail,
                isSponsored: props.data.is_sponsored,
                title: props.data.title,
                subTitleText: _.uniq([...hashtags]).join(' '),
            }
        }

        if (props.platformId === 'twitter') {
            const hashtags = _.map(
                props.data?.tags,
                (item) => `#${item}`,
            )

            const title = props.data.title

            return {
                image: props.data.thumbnail,
                isSponsored: props.data.is_sponsored,
                title: title?.trim() ? title : 'No title',
                subTitleText: hashtags.join(' '),
            }
        }

        if (props.platformId === 'instagram') {
            const title = props.data.title
            return {
                image: props.data.thumbnail,
                isSponsored: props.data.is_sponsored,
                title: title?.trim() ? title : 'No title',
                subTitleText: props.data.tags?.map((item) => `#${item}`)?.join(' '),
            }
        }

    return {}
})

const isLastChild = () => {
    return props.row.getParentRow().original.items.length === (props.row.original.index+1)
}
</script>

<template>
    <div class="relative py-2 pl-4">
        <div
            v-if="row.getIsExpanded() && $lodash.isUndefined(props.row.parentId)"
            class="absolute -bottom-0 -left-0.5 top-1/2 ml-1 h-1/2 w-0.5 divide-none border-none bg-slate-400"
        />

        <template v-else-if="!$lodash.isUndefined(props.row.parentId)">
            <div
                class="absolute -left-[0.15rem] top-1/2 h-0.5 w-2 translate-x-1/2 transform divide-none border-none bg-slate-400"
            >
                <div class="relative h-full w-full">
                    <div class="absolute left-0 top-0 h-full w-2 bg-slate-400" />
                </div>
            </div>

            <div
                class="absolute -left-0.5 top-0 ml-1 h-1/2 w-0.5 divide-none border-none bg-slate-400"
                :class="{ '!h-full': !isLastChild() }"
            />
        </template>

        <div class="flex">
            <component
                :is="(props.link || props.data.cached_content_url) ? 'a' : 'div'"
                :href="props.data.cached_content_url || props.link"
                target="blank"
            >
                <div
                    class="relative flex h-8 w-16 shrink-0 overflow-hidden rounded bg-slate-200 bg-cover bg-center"
                    :style="{
                        'background-image': `url(${mappedData.image})`,
                    }"
                >
                <div v-if="!mappedData.image" class="m-auto text-xs"></div>
                <Tooltip v-if="mappedData.isSponsored" placement="top" max-width="400" class="h-full w-full">
                    <template #trigger>
                        <div
                            class="absolute right-0 top-0 flex h-full w-full bg-gradient-to-b from-transparent to-slate-900 pb-1"
                        >
                            <AppIcon
                                name="dollar-sign"
                                class="mx-auto my-auto h-4 w-4 text-green-300 drop-shadow-lg"
                            />
                        </div>

                        <div class="absolute bottom-0 left-0 h-1 w-full rounded-bl rounded-br bg-green-300"></div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">
                            Creator had a <span class="font-semibold text-white">#sponsored</span> indicator in their
                            title
                        </div>
                    </template>
                </Tooltip>
                <Tooltip v-if="effectiveContentSubType === 'story' && props.data.cached_content_url" placement="top" max-width="400" class="h-full w-full">
                    <template #trigger>
                        <div
                            class="absolute inset-0 flex bg-gradient-to-b from-transparent to-slate-900 pb-1"
                        >
                            <AppIcon
                                name="eye"
                                class="mx-auto my-auto h-4 w-4 text-blue-300 drop-shadow-lg"
                            />
                        </div>
                        <div class="absolute bottom-0 left-0 h-1 w-full rounded-bl rounded-br bg-blue-300"></div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">
                            <span class="font-semibold text-white">Cached story content</span> available - click to view
                        </div>
                    </template>
                </Tooltip>
                <Tooltip v-if="effectiveContentSubType === 'story' && !props.link && !props.data.cached_content_url" placement="top" max-width="400" class="h-full w-full">
                    <template #trigger>
                        <div
                            class="absolute inset-0 flex bg-gradient-to-b from-transparent to-slate-900 pb-1"
                        >
                            <AppIcon
                                name="clock"
                                class="mx-auto my-auto h-4 w-4 text-red-300 drop-shadow-lg"
                            />
                        </div>
                        <div class="absolute bottom-0 left-0 h-1 w-full rounded-bl rounded-br bg-red-300"></div>
                    </template>
                    <template #default>
                        <div class="whitespace-nowrap text-xs">
                            This story has <span class="font-semibold text-white">expired</span> and is no longer available
                        </div>
                    </template>
                </Tooltip>
                </div>
            </component>
            <div class="my-auto ml-3 max-w-32 flex-col overflow-hidden leading-none lg:min-w-48 lg:max-w-80">
                <div class="text-md line-clamp-1 grow whitespace-normal font-semibold" :title="mappedData.title">
                    {{ mappedData.title }}
                </div>
                <div class="line-clamp-1 flex grow whitespace-nowrap text-xs">
                    <span v-if="props.data.items">
                        {{ $lodash.size(props.data.items) }}
                        {{ $pluralize('chapter', $lodash.size(props.data.items)) }}&nbsp;-&nbsp;
                    </span>
                    <span :title="mappedData.subTitleText">
                        {{ mappedData.subTitleText }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>
